import React from "react";
import {
  Box,
  ButtonBase,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import { isHexColor } from "../../data/brand";

// A colour for the public page: optional ready-made swatches, then any
// colour from the picker or a hex code. Empty means `fallback` (the default,
// shown in the picker and as the placeholder). `fallbackValue` is what a
// swatch equal to the default saves (empty, so a later change of the
// default still applies).
const BrandColorField = ({
  label,
  value,
  onChange,
  error,
  helperText,
  presets = [],
  fallback,
}) => {
  const theme = useTheme();
  const current = (isHexColor(value) ? value : fallback).toUpperCase();

  return (
    <Box>
      <Typography
        component="span"
        sx={{
          display: "block",
          fontSize: "0.875rem",
          fontWeight: 600,
          color: theme.customColors.text.strong,
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      {presets.length > 0 && (
        <Stack
          direction="row"
          useFlexGap
          spacing={1}
          role="group"
          aria-label={label}
          sx={{ flexWrap: "wrap", mb: 1.5 }}
        >
          {presets.map((preset) => {
            const selected = current === preset.value.toUpperCase();
            return (
              <Tooltip key={preset.value} title={preset.name} arrow>
                <ButtonBase
                  aria-label={preset.name}
                  aria-pressed={selected}
                  onClick={() =>
                    onChange(
                      preset.value.toUpperCase() === fallback.toUpperCase()
                        ? ""
                        : preset.value
                    )
                  }
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    backgroundColor: preset.value,
                    color: "#FFFFFF",
                    outline: selected ? `2px solid ${preset.value}` : "none",
                    outlineOffset: 2,
                    "&.Mui-focusVisible": {
                      outline: `2px solid ${theme.customColors.text.strong}`,
                    },
                  }}
                >
                  {selected && <CheckIcon sx={{ fontSize: 18 }} />}
                </ButtonBase>
              </Tooltip>
            );
          })}
        </Stack>
      )}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box
          component="label"
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: "8px",
            border: `1px solid ${theme.customColors.border.main}`,
            backgroundColor: current,
            cursor: "pointer",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <input
            type="color"
            aria-label={`Pick the ${label.toLowerCase()}`}
            value={current.toLowerCase()}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            style={{
              position: "absolute",
              inset: 0,
              opacity: 0,
              cursor: "pointer",
            }}
          />
        </Box>
        <TextField
          size="small"
          label={`${label} hex code`}
          placeholder={fallback}
          value={value}
          onChange={(e) => onChange(e.target.value.trim())}
          error={!!error}
          helperText={error || helperText}
          inputProps={{ maxLength: 7, spellCheck: false }}
          sx={{ flex: 1 }}
        />
      </Stack>
    </Box>
  );
};

export default BrandColorField;
